<script>
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';

export default {
    name: 'SubjectContentBlocksEditor',
    components: { AppCard, AppWell },
};
</script>

<script setup>
import QuestionTagContentBlockEditorPanel from '@/Pages/Admin/Categories/partials/QuestionTagContentBlockEditorPanel.vue';

const selectedTags = ref([]);
import { ref } from 'vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import EditTagPanel from '@/Pages/Admin/Categories/partials/EditTagPanel.vue';
import SingleTextModalForm from '@/components/pageSpecific/SingleTextModalForm.vue';
import { IconArrowsDownUp, IconExternalLink, IconChartArrowsVertical, IconTag } from '@tabler/icons-vue';

const showQuestionsCount = ref(true);
const hideTagsWithoutQuestions = ref(true);

function clearSelectedTags() {
    selectedTags.value = [];
}

const selectedTag = ref(null);
function selectTag(tag) {
    if (props.canEdit) {
        selectedTag.value = tag;
    }
}

const props = defineProps({
    subject: {
        type: Object,
        required: true,
    },
    canEdit: {
        type: Boolean,
        required: true,
    },
});
</script>

<template>
    <div>
        <div>
            {{ $tc('messages.content_blocks', 2) }}
        </div>

        <div class="flex justify-end mt-5">
            <InertiaLink :href="subject.links.subject_content_block_order_update">
                <IconArrowsDownUp class="h-4 w-4 text-gray-600" />
            </InertiaLink>
        </div>
        <div class="flex gap-x-2">
            <label class="text-xs">
                <input
                    type="checkbox"
                    name="show_questions_count"
                    id=""
                    v-model="showQuestionsCount"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>{{ $t('messages.show_questions_count') }}</span>
            </label>
            <label class="text-xs">
                <input
                    type="checkbox"
                    name="hide_tags_without_questions"
                    id=""
                    v-model="hideTagsWithoutQuestions"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>{{ $t('messages.hide_tags_without_questions') }}</span>
            </label>
        </div>
        <div>
            <QuestionTagContentBlockEditorPanel
                :selected="selectedTags"
                :subject="subject"
                @close="clearSelectedTags"
            />
        </div>
        <div class="md:grid md:grid-cols-3 md:gap-6 mt-5">
            <div v-for="block in subject.contentBlocks" :key="block.id">
                <AppWell>
                    <AppCard>
                        <div class="p-1">
                            <div class="flex gap-x-2">
                                <div class="font-semibold">{{ block.name }}</div>
                                <div class="flex gap-x-1">
                                    <SingleTextModalForm
                                        :button-text="$t('messages.edit')"
                                        :endpoint="block.edit_endpoint"
                                        :text="block.name"
                                        column="name"
                                        is-edit
                                        use-square-button
                                        v-if="block.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="block.links.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                        v-if="block.can.delete"
                                    />
                                </div>
                            </div>
                            <div>
                                <div v-for="section in block.tags.sections" :key="section.name">
                                    <div>{{ section.name }}</div>
                                    <ul class="ml-3">
                                        <li v-for="tag in section.tags" :key="tag.id" class="pt-1">
                                            <span
                                                class="text-xs flex gap-x-1"
                                                v-show="!hideTagsWithoutQuestions || tag.questions_count > 0"
                                            >
                                                <label>
                                                    <span class="sr-only"> {{ tag.name }}</span>
                                                    <input
                                                        type="checkbox"
                                                        name="tag_id[]"
                                                        :value="tag.id"
                                                        v-model="selectedTags"
                                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                        v-if="canEdit"
                                                    />
                                                </label>
                                                <button @click="selectTag(tag)" class="flex gap-2">
                                                    <BadgePill
                                                        :color="selectedTags.includes(tag.id) ? 'indigo' : 'gray'"
                                                        :title="tag.description"
                                                    >
                                                        {{ tag.name }}
                                                        <span v-if="showQuestionsCount" class="ml-1">
                                                            ({{ tag.questions_count }})
                                                        </span>
                                                    </BadgePill>

                                                    <span
                                                        :title="$tc('messages.links', 2)"
                                                        v-if="tag.form_links.length"
                                                    >
                                                        <IconExternalLink class="h-4 w-4 text-indigo-400" />
                                                    </span>
                                                    <span
                                                        :title="$tc('messages.expected_levels', 2)"
                                                        v-if="tag.expected_levels_count"
                                                    >
                                                        <IconChartArrowsVertical class="h-4 w-4 text-indigo-400" />
                                                    </span>

                                                    <span
                                                        :title="$tc('messages.prerequisites', 2)"
                                                        v-if="tag.tag_ids.length"
                                                    >
                                                        <IconTag class="h-4 w-4 text-indigo-400" />
                                                    </span>
                                                </button>
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <br class="border-gray-400 border-t-2" />

                            <div class="p-3">
                                <div>
                                    <ul>
                                        <li v-for="tag in block.tags.free" :key="tag.id" class="pt-1">
                                            <span
                                                class="text-xs flex gap-x-1"
                                                v-show="!hideTagsWithoutQuestions || tag.questions_count > 0"
                                            >
                                                <label>
                                                    <span class="sr-only"> {{ tag.name }}</span>
                                                    <input
                                                        type="checkbox"
                                                        name="tag_id[]"
                                                        :value="tag.id"
                                                        v-model="selectedTags"
                                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                    />
                                                </label>
                                                <button @click="selectTag(tag)" class="flex gap-2">
                                                    <BadgePill
                                                        :color="selectedTags.includes(tag.id) ? 'indigo' : 'gray'"
                                                        :title="tag.description"
                                                    >
                                                        {{ tag.name }}
                                                        <span v-if="showQuestionsCount" class="ml-1">
                                                            ({{ tag.questions_count }})
                                                        </span>
                                                    </BadgePill>

                                                    <span
                                                        :title="$tc('messages.links', 2)"
                                                        v-if="tag.form_links.length"
                                                    >
                                                        <IconExternalLink class="h-4 w-4 text-indigo-400" />
                                                    </span>

                                                    <span
                                                        :title="$tc('messages.expected_levels', 2)"
                                                        v-if="tag.expected_levels_count"
                                                    >
                                                        <IconChartArrowsVertical class="h-4 w-4 text-indigo-400" />
                                                    </span>

                                                    <span
                                                        :title="$tc('messages.prerequisites', 2)"
                                                        v-if="tag.tag_ids.length"
                                                    >
                                                        <IconTag class="h-4 w-4 text-indigo-400" />
                                                    </span>
                                                </button>
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </AppCard>
                </AppWell>
            </div>
        </div>

        <div>
            <div class="font-bold">{{ $t('messages.tags_without_content_blocks') }}</div>
            <div class="text-gray-600 text-sm">
                {{ $t('messages.tags_without_content_blocks_not_visible_by_students') }}
            </div>

            <div class="flex flex-wrap gap-2">
                <span v-for="tag in subject.tags" :key="tag.id">
                    <span class="text-xs flex gap-x-1" v-show="!hideTagsWithoutQuestions || tag.questions_count > 0">
                        <label>
                            <span class="sr-only"> {{ tag.name }}</span>
                            <input
                                type="checkbox"
                                name="tag_id[]"
                                :value="tag.id"
                                v-model="selectedTags"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                v-if="canEdit"
                            />
                        </label>
                        <button @click="selectTag(tag)" class="flex gap-2">
                            <BadgePill
                                :color="selectedTags.includes(tag.id) ? 'indigo' : 'gray'"
                                :title="tag.description"
                            >
                                {{ tag.name }}
                                <span v-if="showQuestionsCount" class="ml-1">({{ tag.questions_count }})</span>
                            </BadgePill>

                            <span :title="$tc('messages.links', 2)" v-if="tag.form_links.length">
                                <IconExternalLink class="h-4 w-4 text-indigo-400" />
                            </span>
                            <span :title="$tc('messages.expected_levels', 2)" v-if="tag.expected_levels_count">
                                <IconChartArrowsVertical class="h-4 w-4 text-indigo-400" />
                            </span>

                            <span :title="$tc('messages.prerequisites', 2)" v-if="tag.tag_ids.length">
                                <IconTag class="h-4 w-4 text-indigo-400" />
                            </span>
                        </button>
                    </span>
                </span>
            </div>
        </div>

        <template v-if="selectedTag">
            <EditTagPanel :tag="selectedTag" :tags="subject.all_tags" @close="selectedTag = null" />
        </template>
    </div>
</template>
